import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings as SettingsIcon, Building2, Receipt, Users, 
  Sun, Moon, Loader2, Save
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { api, organization, user, hasRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgSettings, setOrgSettings] = useState({
    name: '',
    tin: '',
    address: '',
    phone: '',
    vat_mode: 'non_vat',
    vat_rate: 12,
    receipt_header: '',
    receipt_footer: '',
    bir_effective_date: '',
    bir_accreditation_number: '',
    bir_ptu_number: '',
    bir_serial_number: '',
    bir_machine_id_number: '',
    bir_branch_code: '',
    registered_business_address: '',
    registered_business_name: '',
  });

  useEffect(() => {
    if (organization) {
      setOrgSettings({
        name: organization.name || '',
        tin: organization.tin || '',
        address: organization.address || '',
        phone: organization.phone || '',
        vat_mode: organization.vat_mode || 'non_vat',
        vat_rate: organization.vat_rate || 12,
        receipt_header: organization.receipt_header || '',
        receipt_footer: organization.receipt_footer || '',
        registered_business_name: organization.registered_business_name || '',
        registered_business_address: organization.registered_business_address || '',
        bir_branch_code: organization.bir_branch_code || '',
        bir_machine_id_number: organization.bir_machine_id_number || '',
        bir_serial_number: organization.bir_serial_number || '',
        bir_ptu_number: organization.bir_ptu_number || '',
        bir_accreditation_number: organization.bir_accreditation_number || '',
        bir_effective_date: organization.bir_effective_date ? organization.bir_effective_date.slice(0, 10) : '',
      });
    }
  }, [organization]);

  const handleSaveOrg = async () => {
    setSaving(true);
    try {
      await api.patch('/tenant/organization', orgSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your store settings</p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" className="gap-2">
            <SettingsIcon className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="organization" className="gap-2">
            <Building2 className="w-4 h-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="receipt" className="gap-2">
            <Receipt className="w-4 h-4" />
            Receipt
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>App preferences and display settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                <div className="flex items-center gap-4">
                  {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  <div>
                    <Label className="text-base">Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      {theme === 'dark' ? 'Dark theme is active' : 'Light theme is active'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
              </div>

              {/* User Info */}
              <div className="p-4 rounded-lg border border-border">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-sm text-muted-foreground">{user?.email}</div>
                    <Badge variant="secondary" className="mt-1 capitalize">{user?.role}</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organization Settings */}
        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Business information and tax settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Name</Label>
                  <Input
                    value={orgSettings.name}
                    onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
                    className="mt-1"
                    disabled={!hasRole('owner')}
                  />
                </div>
                <div>
                  <Label>TIN (Tax Identification Number)</Label>
                  <Input
                    value={orgSettings.tin}
                    onChange={(e) => setOrgSettings({ ...orgSettings, tin: e.target.value })}
                    placeholder="e.g., 123-456-789-000"
                    className="mt-1"
                    disabled={!hasRole('owner')}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={orgSettings.phone}
                    onChange={(e) => setOrgSettings({ ...orgSettings, phone: e.target.value })}
                    placeholder="e.g., +63 912 345 6789"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>VAT Mode</Label>
                  <Select 
                    value={orgSettings.vat_mode} 
                    onValueChange={(value) => setOrgSettings({ ...orgSettings, vat_mode: value })}
                    disabled={!hasRole('owner')}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_vat">Non-VAT</SelectItem>
                      <SelectItem value="vat_registered">VAT Registered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {orgSettings.vat_mode === 'vat_registered' && (
                  <div>
                    <Label>VAT Rate (%)</Label>
                    <Input
                      type="number"
                      value={orgSettings.vat_rate}
                      onChange={(e) => setOrgSettings({ ...orgSettings, vat_rate: parseFloat(e.target.value) })}
                      className="mt-1"
                      disabled={!hasRole('owner')}
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={orgSettings.address}
                    onChange={(e) => setOrgSettings({ ...orgSettings, address: e.target.value })}
                    placeholder="Business address"
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>

              {hasRole('owner', 'manager') && (
                <Button onClick={handleSaveOrg} disabled={saving} className="mt-4" data-testid="save-org-settings">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipt Settings */}
        <TabsContent value="receipt">
          <Card>
            <CardHeader>
              <CardTitle>Receipt Settings</CardTitle>
              <CardDescription>Customize your receipt appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Receipt Header</Label>
                <Textarea
                  value={orgSettings.receipt_header}
                  onChange={(e) => setOrgSettings({ ...orgSettings, receipt_header: e.target.value })}
                  placeholder="Text to show at top of receipt"
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label>Receipt Footer</Label>
                <Textarea
                  value={orgSettings.receipt_footer}
                  onChange={(e) => setOrgSettings({ ...orgSettings, receipt_footer: e.target.value })}
                  placeholder="Text to show at bottom of receipt (e.g., Thank you!)"
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">BIR Registration Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Registered Business Name</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.registered_business_name || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, registered_business_name: e.target.value })}
                      placeholder="Legal registered seller name"
                    />
                  </div>
                  <div>
                    <Label>BIR Branch Code</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_branch_code || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_branch_code: e.target.value })}
                      placeholder="00000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Registered Business Address</Label>
                    <Textarea
                      value={orgSettings.registered_business_address || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, registered_business_address: e.target.value })}
                      placeholder="Complete registered business address"
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Machine Identification Number (MIN)</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_machine_id_number || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_machine_id_number: e.target.value })}
                      placeholder="MIN / Machine ID"
                    />
                  </div>
                  <div>
                    <Label>Serial Number</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_serial_number || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_serial_number: e.target.value })}
                      placeholder="Software or machine serial number"
                    />
                  </div>
                  <div>
                    <Label>PTU Number</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_ptu_number || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_ptu_number: e.target.value })}
                      placeholder="Permit to Use number"
                    />
                  </div>
                  <div>
                    <Label>Accreditation Number</Label>
                    <input
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_accreditation_number || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_accreditation_number: e.target.value })}
                      placeholder="BIR accreditation number"
                    />
                  </div>
                  <div>
                    <Label>PTU Effective Date</Label>
                    <input
                      type="date"
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={orgSettings.bir_effective_date || ''}
                      onChange={(e) => setOrgSettings({ ...orgSettings, bir_effective_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6">
                <Label>Preview</Label>
                <div className="mt-2 p-4 rounded-lg bg-white text-black text-center text-sm border">
                  <div className="font-bold">{orgSettings.registered_business_name || orgSettings.name || 'Store Name'}</div>
                  {(orgSettings.registered_business_address || orgSettings.address) && (
                    <div className="text-xs">{orgSettings.registered_business_address || orgSettings.address}</div>
                  )}
                  {orgSettings.tin && (
                    <div className="text-xs">
                      {orgSettings.vat_mode === 'vat_registered' ? 'VAT REG TIN' : 'NON VAT REG TIN'}: {orgSettings.tin}
                      {orgSettings.bir_branch_code ? `-${orgSettings.bir_branch_code}` : ''}
                    </div>
                  )}
                  {orgSettings.bir_machine_id_number && <div className="text-xs">MIN: {orgSettings.bir_machine_id_number}</div>}
                  {orgSettings.bir_serial_number && <div className="text-xs">SN: {orgSettings.bir_serial_number}</div>}
                  {orgSettings.bir_ptu_number && <div className="text-xs">PTU No.: {orgSettings.bir_ptu_number}</div>}
                  {orgSettings.bir_accreditation_number && <div className="text-xs">Accreditation No.: {orgSettings.bir_accreditation_number}</div>}
                  {orgSettings.receipt_header && (
                    <div className="mt-2 text-xs border-t border-dashed pt-2">{orgSettings.receipt_header}</div>
                  )}
                  <div className="my-4 text-xs text-gray-500">... receipt items ...</div>
                  {orgSettings.receipt_footer && (
                    <div className="text-xs border-t border-dashed pt-2">{orgSettings.receipt_footer}</div>
                  )}
                </div>
              </div>

              {hasRole('owner', 'manager') && (
                <Button onClick={handleSaveOrg} disabled={saving} className="mt-4" data-testid="save-receipt-settings">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
